import type React from 'react';
import { useState } from 'react';
import { PlannedReportsPanel } from './components/PlannedReportsPanel';
import { ComparisonReportsPanel } from './components/ComparisonReportsPanel';
import { TipReportsPanel } from './components/TipReportsPanel';
import classes from './manager-reports-page.module.scss';
import { minutesLabel, timeLabel } from './manager-reports-page.utils';
import { useManagerReportsPageController } from './useManagerReportsPageController';

export const ManagerReportsPage: React.FC = () => {
  const [mode,setMode] = useState<'actual' | 'planned' | 'comparison' | 'tips'>('actual');
  const modes = ['actual','planned','comparison','tips'] as const;
  return <>
    <div className="workforceToolbar" role="tablist" aria-label="סוג דוח" dir="rtl" onKeyDown={event => {
      if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault();
      const index = event.key==='Home' ? 0 : event.key==='End' ? modes.length-1 : (modes.indexOf(mode)+(event.key==='ArrowLeft' ? 1 : modes.length-1))%modes.length;
      setMode(modes[index]);event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[index]?.focus();
    }}>
      <button type="button" role="tab" id="actual-report-tab" aria-controls="manager-report-panel" tabIndex={mode==='actual' ? 0 : -1}
        aria-selected={mode==='actual'} className={mode==='actual' ? 'primaryAction' : 'secondaryAction'}
        onClick={() => setMode('actual')}>נוכחות בפועל</button>
      <button type="button" role="tab" id="planned-report-tab" aria-controls="manager-report-panel" tabIndex={mode==='planned' ? 0 : -1}
        aria-selected={mode==='planned'} className={mode==='planned' ? 'primaryAction' : 'secondaryAction'}
        onClick={() => setMode('planned')}>תכנון עבודה</button>
      <button type="button" role="tab" id="comparison-report-tab" aria-controls="manager-report-panel" tabIndex={mode==='comparison' ? 0 : -1}
        aria-selected={mode==='comparison'} className={mode==='comparison' ? 'primaryAction' : 'secondaryAction'}
        onClick={() => setMode('comparison')}>תכנון מול ביצוע</button>
      <button type="button" role="tab" id="tips-report-tab" aria-controls="manager-report-panel" tabIndex={mode==='tips' ? 0 : -1}
        aria-selected={mode==='tips'} className={mode==='tips' ? 'primaryAction' : 'secondaryAction'} onClick={() => setMode('tips')}>טיפים</button>
    </div>
    <div role="tabpanel" id="manager-report-panel" aria-labelledby={`${mode}-report-tab`}>
      {mode==='actual' ? <ActualManagerReports /> : mode==='planned' ? <PlannedReportsPanel /> : mode==='comparison' ? <ComparisonReportsPanel /> : <TipReportsPanel />}
    </div>
  </>;
};

const ActualManagerReports: React.FC = () => {
  const {
    from,
    setFrom,
    to,
    setTo,
    employeePk,
    setEmployeePk,
    isLoading,
    hasMore,
    isLoadingMore,
    isExporting,
    onLoadMore,
    error,
    employees,
    filtered,
    totals,
    onExportExcel,
    isOutdated,
    onRefresh,
  } = useManagerReportsPageController();

  return (
    <section className="reportsPage" dir="rtl">
      <div className="reportsHeader">
        <div>
          <h1>דוחות</h1>
          <p>נוכחות בפועל ותוצאות חלוקת טיפים שמורות</p>
        </div>
        <button
          type="button"
          className="secondaryAction"
          disabled={!filtered.length || isExporting}
          onClick={() => void onExportExcel()}
        >
          ייצוא לאקסל
        </button>
      </div>
      <div className={`workforceToolbar ${classes.managerReportToolbar}`}>
        <label>
          מתאריך
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          עד תאריך
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <label>
          עובד
          <select
            value={employeePk ?? ''}
            onChange={(event) => setEmployeePk(event.target.value ? Number(event.target.value) : null)}
          >
            <option value="">כל העובדים</option>
            {employees.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="reportSummaryStrip managerReportSummary">
        <div>
          <span>שעות בפועל</span>
          <strong>{minutesLabel(totals.worked)}</strong>
        </div>
        <div>
          <span>טיפ לשעה</span>
          <strong>₪{(totals.worked > 0 ? totals.tips * 60 / totals.worked : 0).toFixed(2)}</strong>
        </div>
        <div>
          <span>טיפים מחושבים</span>
          <strong>₪{totals.tips.toFixed(2)}</strong>
        </div>
      </div>
      {error && <p className="errorText reportsState">{error}</p>}
      {isOutdated && <button type="button" className="secondaryAction" disabled={isLoading} onClick={onRefresh}>עדכון הדוח</button>}
      {isLoading && <p className="reportsState">טוען דוח...</p>}
      {!isLoading && !error && (
        <div className="reportTableWrap">
          <table role="table" className="reportTable managerReportTable">
            <caption>
              {from} עד {to}
            </caption>
            <thead>
              <tr>
                <th>עובד</th>
                <th>תאריך</th>
                <th>מחלקה</th>
                <th>כניסה</th>
                <th>יציאה</th>
                <th>משך בפועל</th>
                <th>טיפים</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td data-label="עובד">
                    <strong>{row.employeeName}</strong>
                  </td>
                  <td data-label="תאריך">{row.date}</td>
                  <td data-label="מחלקה">
                    {row.roleName}
                  </td>
                  <td data-label="כניסה">{timeLabel(row.actualStart)}</td>
                  <td data-label="יציאה">
                    {timeLabel(row.actualEnd)}
                    {row.autoClosedAt && <small className={classes.autoCloseNote}>נסגר אוטומטית</small>}
                  </td>
                  <td data-label="משך בפועל">{minutesLabel(row.workedMinutes)}</td>
                  <td data-label="טיפים">₪{Number(row.tipTotal).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="reportsState">אין נתונים בטווח שנבחר.</p>}
        </div>
      )}
      {hasMore && !isLoading && <button type="button" className="secondaryAction" disabled={isLoadingMore} onClick={() => void onLoadMore()}>טעינת רישומים נוספים</button>}
    </section>
  );
};
